import { Injectable } from '@angular/core';
import { ServiceLocator, IAppConfig } from 'portalServices';
import { getWebServiceConfig } from './service.config.web';

import { SpinnerService } from 'portalServices';
import { AuthenticationService } from 'portalServices';
import { AuthStatusService } from 'portalServices';
import { ConfigService } from 'portalServices';
import { InitializationService } from 'portalServices';
import { AccountManagerService } from 'portalServices';
import { AccountingService } from 'portalServices';
import { AnouncementService } from 'portalServices';
import { BenchEmployeeService } from 'portalServices';
import { ClientService } from 'portalServices';
import { DashboardService } from 'portalServices';
import { DemandService } from 'portalServices';
import { DemandRoleService } from 'portalServices';
import { DownloadService } from 'portalServices';
import { EmailService } from 'portalServices';
import { EmailAccountService } from 'portalServices';
import { EmployeeService } from 'portalServices';
import { HelpDeskService } from 'portalServices';
import { HMFlowService } from 'portalServices';
import { ImmigrationService } from 'portalServices';
import { InsuranceProviderService } from 'portalServices';
import { InvoiceService } from 'portalServices';
import { PayrollService } from 'portalServices';
import { ProjectService } from 'portalServices';
import { PropertyService } from 'portalServices';
import { ResourceService } from 'portalServices';
import { SalesRepService } from 'portalServices';
import { UserManagementService } from 'portalServices';
import { UserService } from 'portalServices';
import { VendorService } from 'portalServices';
import { AttendanceService } from 'portalServices';

/**
 * AppServices - Centralized service management with environment-based configuration
 * 
 * ? Initializes ServiceLocator with environment-based configuration
 * ? Provides clean API for components to access all services
 * ? Services automatically have correct dependencies
 * ? Shared HttpProvider across all services
 * ? Automatic token sharing via shared interceptors
 * 
 * Features:
 * - Singleton pattern (initialized once)
 * - Environment-based configuration (dev, staging, prod)
 * - Clean dependency injection
 * - Proper logout/cleanup
 * - Debug logging
 * 
 * Usage:
 * 1. Inject AppServices in component
 * 2. Access services via getters: appServices.auth, appServices.userManagement
 * 3. All services use shared HttpProvider and tokens
 * 
 * Example:
 * constructor(private appServices: AppServices) {
 *     const userService = appServices.userManagement;
 *     userService.getAllUsers().subscribe(...);
 * }
 */
@Injectable({ providedIn: 'root' })
export class AppServices {
    private static isInitialized = false;
    private appConfig: IAppConfig;

    constructor() {
        this.appConfig = getWebServiceConfig();

        if (!AppServices.isInitialized) {
            ServiceLocator.initialize(this.appConfig);
            AppServices.isInitialized = true;
        }
    }

    public get locator(): ServiceLocator {
        return ServiceLocator.getInstance();
    }

    // ??? Tier 1 ???????????????????????????????????????????????????????????????

    get spinnerService(): SpinnerService {
        return this.locator.getSpinnerService();
    }

    get authenticationService(): AuthenticationService {
        return this.locator.getAuthenticationService();
    }

    get configService(): ConfigService {
        return this.locator.getConfigService();
    }

    // ??? Tier 2 ???????????????????????????????????????????????????????????????

    get authStatusService(): AuthStatusService {
        return this.locator.getAuthStatusService();
    }

    get accountManagerService(): AccountManagerService {
        return this.locator.getAccountManagerService();
    }

    get accountingService(): AccountingService {
        return this.locator.getAccountingService();
    }

    get anouncementService(): AnouncementService {
        return this.locator.getAnouncementService();
    }

    get benchEmployeeService(): BenchEmployeeService {
        return this.locator.getBenchEmployeeService();
    }

    get clientService(): ClientService {
        return this.locator.getClientService();
    }

    get dashboardService(): DashboardService {
        return this.locator.getDashboardService();
    }

    get demandService(): DemandService {
        return this.locator.getDemandService();
    }

    get demandRoleService(): DemandRoleService {
        return this.locator.getDemandRoleService();
    }

    get downloadService(): DownloadService {
        return this.locator.getDownloadService();
    }

    get emailService(): EmailService {
        return this.locator.getEmailService();
    }

    get emailAccountService(): EmailAccountService {
        return this.locator.getEmailAccountService();
    }

    get employeeService(): EmployeeService {
        return this.locator.getEmployeeService();
    }

    get attendanceService():AttendanceService{
        return this.locator.getAttendanceService();
    }

    get helpDeskService(): HelpDeskService {
        return this.locator.getHelpDeskService();
    }

    get hmFlowService(): HMFlowService {
        return this.locator.getHMFlowService();
    }

    get immigrationService(): ImmigrationService {
        return this.locator.getImmigrationService();
    }

    get insuranceProviderService(): InsuranceProviderService {
        return this.locator.getInsuranceProviderService();
    }

    get invoiceService(): InvoiceService {
        return this.locator.getInvoiceService();
    }

    get payrollService(): PayrollService {
        return this.locator.getPayrollService();
    }

    get propertyService(): PropertyService {
        return this.locator.getPropertyService();
    }

    get resourceService(): ResourceService {
        return this.locator.getResourceService();
    }

    get salesRepService(): SalesRepService {
        return this.locator.getSalesRepService();
    }

    get userManagementService(): UserManagementService {
        return this.locator.getUserManagementService();
    }

    get userService(): UserService {
        return this.locator.getUserService();
    }

    get vendorService(): VendorService {
        return this.locator.getVendorService();
    }

    // ??? Tier 3 ???????????????????????????????????????????????????????????????

    get projectService(): ProjectService {
        return this.locator.getProjectService();
    }

    get initializationService(): InitializationService {
        return this.locator.getInitializationService();
    }

    // ??? Lifecycle ????????????????????????????????????????????????????????????

    logout(): void {
        ServiceLocator.destroy();
        AppServices.isInitialized = false;
    }

    getConfig(): IAppConfig {
        return this.appConfig;
    }

    getStats() {
        return ServiceLocator.getStats();
    }
}
